import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Users, LogOut, GraduationCap, Sparkles, UserCog, ClipboardList, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import MainSiteLink from "@/components/MainSiteLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const changePassword = async () => {
    if (next.length < 6) { toast.error(t("admin.password.too_short")); return; }
    if (next !== confirm) { toast.error(t("admin.password.mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t("admin.password.updated"));
      setPwOpen(false); setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.password.failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 border-r border-border bg-card flex flex-col fixed h-screen">
        <div className="flex items-center gap-2 px-6 py-6">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-base font-semibold tracking-tight">{t("brand_ats")}</span>
        </div>
        <MainSiteLink className="px-6 pb-4" />
        <nav className="flex-1 px-3 space-y-1">
          <NavLink to="/admin" end className={linkClass} data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {t("admin.nav.dashboard")}
          </NavLink>
          <NavLink to="/admin/applications" className={linkClass} data-testid="nav-applications">
            <Users className="w-4 h-4" strokeWidth={1.5} /> {t("admin.nav.applications")}
          </NavLink>
          <NavLink to="/admin/results" className={linkClass} data-testid="nav-results">
            <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> {t("admin.nav.results")}
          </NavLink>
          <NavLink to="/admin/ai" className={linkClass} data-testid="nav-ai">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} /> {t("admin.nav.ai")}
          </NavLink>
          <NavLink to="/admin/evaluators" className={linkClass} data-testid="nav-evaluators">
            <UserCog className="w-4 h-4" strokeWidth={1.5} /> {t("admin.nav.evaluators")}
          </NavLink>
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={() => setPwOpen(true)} data-testid="change-password-btn">
            <KeyRound className="w-4 h-4 mr-3" strokeWidth={1.5} /> {t("admin.password.change")}
          </Button>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-3" strokeWidth={1.5} /> {t("common.sign_out")}
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-64 p-8 custom-scroll overflow-auto">
        <Outlet />
      </main>

      <Dialog open={pwOpen} onOpenChange={(o) => { if (!o) { setPwOpen(false); setCur(""); setNext(""); setConfirm(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("admin.password.change")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm mb-1.5 block">{t("admin.password.current")}</Label>
              <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="h-11" data-testid="cur-password-input" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">{t("admin.password.new")}</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={t("admin.password.new_placeholder")} className="h-11" data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">{t("admin.password.confirm")}</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={changePassword} disabled={saving || !cur || !next} data-testid="save-password-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.password.update")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
